// REACT
import { useEffect } from "react";

// MATERIAL-UI
import { Container, Grid, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";

// SHARED COMPONENTS
import Title from "src/components/common/Title";
import Layout from "src/components/core/Layout";
import MaintenanceBlock from "src/components/maintenance/MaintenanceBlock";
import { OrderHistory } from "./components/OrderHistory";
import StrategiesTable from "./scheduled-orders/components/StrategiesTable";

// FEATURE COMPONENTS
import { Disclaimer } from "./components/Disclaimer";
import CryptoRiskWarning from "src/components/disclosures/CryptoRiskWarning";
import { OrderHistoryTargets } from "./targets/components/OrderHistoryTargets";

// SUB-FEATURES
import { Swap } from "./swap/Swap";
import { Targets } from "./targets/Targets";
import CreateStrategyComponent from "./scheduled-orders/components/CreateStrategyComponent";

// REDUX
import { useMaintenanceState } from "src/store/features/maintenance.slice";
import { setTradeTab, useTradeSlice } from "src/store/features/trade.slice";
import { useDispatch } from "react-redux";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// CONSTANTS
import { SUBTITLE } from "./constants";

interface ITradeTab {
  id: "swap" | "targets" | "scheduled";
  label: string;
  component: React.FC;
  tradesComponent: React.FC;
}
const TABS: ITradeTab[] = [
  { id: "swap", label: "Swap", component: Swap, tradesComponent: OrderHistory },
  { id: "targets", label: "Target", component: Targets, tradesComponent: OrderHistoryTargets },
  {
    id: "scheduled",
    label: "Schedule",
    component: CreateStrategyComponent,
    tradesComponent: StrategiesTable,
  },
];

export const ComponentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  gap: "22px",
}));

const TabsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "8px 12px",
  gap: "22px",
  borderRadius: "12px",
  marginBottom: "28px",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
  [theme.breakpoints.down("xs")]: {
    width: "100%",
    gap: "12px",
  },
}));

interface TabItemProps {
  active?: boolean;
}
const TabItem = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active",
})<TabItemProps>(({ theme, active }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  cursor: "pointer",
  padding: "8px 28px",
  fontSize: "14px",
  fontWeight: 400,
  height: "32px",
  color: active ? "#FFFFFF" : "#000000",
  backgroundColor: active ? "#002035" : "#FFFFFF",
  borderBottom: active ? `2px solid ${theme.palette.primary.main}` : "none",
  borderRadius: "12px",
  lineHeight: "16px",
  "&:hover": {
    backgroundColor: active ? "#002035" : "#E8F6FF",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
  },
}));

const ActiveTabDot = styled("span")(({ theme }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#FC4C4C",
  marginLeft: "6px",
}));

/**
 * TradePage component that serves as the main entry point for the trading features.
 * Features include swap, target and scheduled orders.
 *
 * @returns {JSX.Element} - The TradePage component.
 */
export const TradePage = (): JSX.Element => {
  const { selectedTab } = useTradeSlice();
  const { tradecontroller } = useMaintenanceState();
  const dispatch = useDispatch();

  const currentTab = TABS.find((tab) => tab.id === selectedTab) || TABS[0];
  const ActiveComponent = currentTab.component;
  const ActiveComponentTrades = currentTab.tradesComponent;

  const title = {
    partA: `${currentTab.label} with`,
    partB: "caleb & brown",
  };

  useEffect(() => {
    trackEvent("Visit Page", { page: "Trade" });
  }, []);

  return (
    <Layout>
      {!tradecontroller && <MaintenanceBlock page={"trade"} />}
      <Container>
        {/* Title */}
        <Box>
          <Title title={title} subtitle={SUBTITLE} />
        </Box>
        {/* UK-only high-risk warning */}
        <CryptoRiskWarning />
        <ComponentWrapper>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={12}
              lg={6}
              sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <TabsContainer>
                {TABS.map((tab) => (
                  <TabItem
                    key={tab.id}
                    active={selectedTab === tab.id}
                    onClick={() => dispatch(setTradeTab(tab.id))}>
                    {tab.label}
                    {selectedTab === tab.id && <ActiveTabDot />}
                  </TabItem>
                ))}
              </TabsContainer>
              <ActiveComponent />
            </Grid>
            <Grid item xs={12} md={12} lg={6} mb={6} sx={{ margin: "0px" }}>
              <ActiveComponentTrades />
            </Grid>
          </Grid>
          <Disclaimer />
        </ComponentWrapper>
      </Container>
    </Layout>
  );
};
